import { BRAND } from "@/config/brand";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavLink {
  label: string;
  to: string;
}

const navLinks: readonly NavLink[] = [
  { label: "Products", to: "/products" },
  { label: "Company", to: "/company" },
  { label: "Blog", to: "/blog" },
] as const;

const THEME_STORAGE_KEY = "corgi-theme";

export function EtfNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        hamburgerRef.current &&
        !hamburgerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMenuOpen]);

  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem(THEME_STORAGE_KEY) as "light" | "dark") || "dark";
    }
    return "dark";
  });

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    if (next === "dark") {
      document.documentElement.removeAttribute("data-theme");
    } else {
      document.documentElement.setAttribute("data-theme", next);
    }
    localStorage.setItem(THEME_STORAGE_KEY, next);
  };

  return (
    <>
      <nav className="sticky top-0 z-[200] flex items-center h-14 px-10 max-nav:px-5 border-b border-[var(--etf-border)] bg-[var(--etf-black)]">
        <Link to="/" className="flex items-center mr-12 no-underline">
          <img
            src={BRAND.logos.dark}
            alt="Corgi Funds"
            className="h-7 w-auto block dark-only-logo"
            loading="eager"
          />
          <img
            src={BRAND.logos.light}
            alt="Corgi Funds"
            className="h-7 w-auto block light-only-logo"
            loading="eager"
          />
        </Link>

        <ul className="flex items-center flex-1 list-none gap-0 max-nav:!hidden">
          {navLinks.map((link, i) => {
            const linkClass = cn(
              "text-sm font-[family-name:var(--font-instrument)] text-[var(--etf-white2)] no-underline tracking-[0.02em] hover:text-[var(--etf-white)] transition-colors",
              pathname.startsWith(link.to) && "text-[var(--etf-white)]",
            );
            return (
              <li key={link.label} className="flex items-center">
                <Link to={link.to} className={linkClass}>
                  {link.label}
                </Link>
                {i < navLinks.length - 1 && (
                  <span className="mx-3 text-[10px] text-[var(--etf-white3)] opacity-50 tracking-[-3px]">
                    ——
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="bg-transparent border border-[var(--etf-border)] text-[var(--etf-white2)] w-9 h-9 flex items-center justify-center cursor-pointer mr-3 p-0 shrink-0 transition-all duration-150 hover:border-[var(--etf-white2)] hover:text-[var(--etf-white)]"
          onClick={toggleTheme}
          aria-label="Toggle light/dark mode"
        >
          {theme === "light" && (
            <svg
              aria-hidden="true"
              focusable="false"
              className="w-4 h-4 block"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          )}
          {theme === "dark" && (
            <svg
              aria-hidden="true"
              focusable="false"
              className="w-4 h-4 block"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>

        <button
          type="button"
          className="max-nav:!hidden px-3.5 h-9 inline-flex items-center text-[16px] font-medium font-[family-name:var(--font-instrument)] tracking-[0.04em] cursor-default transition-colors duration-150 border border-[var(--etf-border)] bg-transparent text-[var(--etf-white)] mr-3"
        >
          Contact Us
        </button>

        <button
          ref={hamburgerRef}
          type="button"
          className="hidden max-nav:!flex flex-col justify-center items-center gap-[5px] w-9 h-9 bg-transparent border-none cursor-pointer p-1 shrink-0 ml-3"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span
            className={cn(
              "block w-5 h-[1.5px] bg-[var(--etf-white)] transition-all duration-[250ms] origin-center",
              isMenuOpen && "translate-y-[6.5px] rotate-45",
            )}
          />
          <span
            className={cn(
              "block w-5 h-[1.5px] bg-[var(--etf-white)] transition-all duration-[250ms] origin-center",
              isMenuOpen && "opacity-0 scale-x-0",
            )}
          />
          <span
            className={cn(
              "block w-5 h-[1.5px] bg-[var(--etf-white)] transition-all duration-[250ms] origin-center",
              isMenuOpen && "-translate-y-[6.5px] -rotate-45",
            )}
          />
        </button>
      </nav>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className="fixed top-14 left-0 right-0 bg-[var(--etf-black)] border-b border-[var(--etf-border)] z-[198] flex flex-col px-5 pt-2 pb-4 shadow-[0_8px_24px_rgba(0,0,0,0.4)]"
        >
          {navLinks.map((link, i) => {
            const isLast = i === navLinks.length - 1;
            return (
              <Link
                key={link.label}
                to={link.to}
                className={cn(
                  "font-[family-name:var(--font-instrument)] text-[15px] text-[var(--etf-white2)] no-underline py-3.5 tracking-[0.04em] transition-colors duration-150 hover:text-[var(--etf-white)]",
                  !isLast && "border-b border-[var(--etf-border2)]",
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            );
          })}
          <button
            type="button"
            className="text-left font-[family-name:var(--font-instrument)] text-[15px] text-[var(--etf-white2)] py-3.5 tracking-[0.04em] bg-transparent border-none border-t border-[var(--etf-border2)] cursor-default p-0"
          >
            Contact Us
          </button>
        </div>
      )}
    </>
  );
}
